import React from 'react';
import { Box, InputBase, InputBaseProps, Typography } from '@mui/material';

interface TextInputProps extends InputBaseProps {
  label: string;
  /** Optional control rendered at the trailing edge (e.g. a show-password button). */
  endAdornment?: React.ReactNode;
}

/**
 * Labeled text field styled to match the design system inputs:
 * 48px tall, 8px radius, subtle border and a muted placeholder.
 */
export default function TextInput({ label, endAdornment, id, error, ...inputProps }: TextInputProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
      <Typography
        component="label"
        htmlFor={id}
        variant="body2"
        sx={{ fontWeight: 500, color: 'text.secondary' }}
      >
        {label}
      </Typography>
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          minHeight: 48,
          px: 2,
          backgroundColor: theme.app.input,
          border: `1px solid ${error ? theme.app.destructive : theme.app.border}`,
          borderRadius: `${theme.shape.borderRadius}px`,
          transition: theme.transitions.create(['border-color', 'box-shadow']),
          '&:focus-within': {
            borderColor: error ? theme.app.destructive : theme.app.ring,
            boxShadow: `0 0 0 1px ${error ? theme.app.destructive : theme.app.ring}`,
          },
        })}
      >
        <InputBase
          id={id}
          fullWidth
          error={error}
          sx={(theme) => ({
            fontSize: 16,
            lineHeight: '24px',
            color: theme.app.foreground,
            '& input::placeholder': {
              color: theme.app.mutedForeground,
              opacity: 1,
            },
          })}
          {...inputProps}
        />
        {endAdornment}
      </Box>
    </Box>
  );
}
